"use client";

import { AvatarNameBadge } from "@/components/ui/avatar-name-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { Paper } from "@/components/ui/paper";
import type { ScorecardFillOut } from "@/lib/api/model";

import { RECOMMENDATION_LABELS, type ScorecardField } from "../../lib/types";
import { shortDate } from "../shared";

export function ScorecardFillCard({ fill, fields, isOwn, onEdit }: { fill: ScorecardFillOut; fields: ScorecardField[]; isOwn: boolean; onEdit?: () => void }) {
  const submittedOn = shortDate(fill.submitted_at);
  const render = (f: ScorecardField, v: unknown) => {
    if (v == null || v === "") return "—";
    if (f.type === "rubric_score") { const lv = f.levels?.find((l) => l.value === v); return lv ? `${v} — ${lv.anchor}` : String(v); }
    if (f.type === "yes_no") return v ? "Yes" : "No";
    if (f.type === "overall_recommendation") return RECOMMENDATION_LABELS[v as keyof typeof RECOMMENDATION_LABELS] ?? String(v);
    return String(v);
  };

  return (
    <Paper.Container className="flex-col items-stretch gap-3 md:flex-col md:items-stretch">
      <div className="flex items-start gap-3">
        <Paper.Body>
          <Paper.TitleRow className="flex-wrap">
            <AvatarNameBadge name={fill.interviewer.name} />
            {fill.submitted_at ? (
              <Badge variant="success" size="xs" className="rounded-sm">
                Submitted
              </Badge>
            ) : (
              <Badge variant="outline-muted" size="xs" className="rounded-sm">
                Draft
              </Badge>
            )}
            <Badge variant="outline-muted" size="xs" className="rounded-sm">
              {fill.source === "conversation" ? "From conversation" : fill.source === "manual" ? "Manual" : fill.source}
            </Badge>
          </Paper.TitleRow>
          <Paper.Description>
            {fill.external_event_id ?? "Conversation"}
            {submittedOn ? ` · submitted ${submittedOn}` : " · not submitted yet"}
          </Paper.Description>
        </Paper.Body>
        {isOwn && onEdit && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onEdit}
            icon={<Icon name="IconPencil" size={15} />}
          >
            Edit
          </Button>
        )}
      </div>

      <dl className="grid grid-cols-1 gap-x-4 gap-y-2 border-t border-border pt-3 sm:grid-cols-[minmax(6rem,11rem)_1fr]">
        {fields.map((f) => (
          <div key={f.key} className="grid grid-cols-subgrid sm:col-span-2">
            <dt className="text-[13px] text-contrast/50">{f.label}</dt>
            <dd className="text-sm text-contrast/80">
              {render(f, (fill.values as Record<string, unknown>)[f.key])}
            </dd>
          </div>
        ))}
      </dl>

      {fill.conversation_id && (
        <p
          className="text-[13px] text-contrast/40"
          title="Conversation pages ship with spec 02"
        >
          Open conversation
        </p>
      )}
    </Paper.Container>
  );
}
